/**
 * Read-adapter from the real `personalPayroll.listEmployerLinks` /
 * `personalPayroll.listPayslips` wire shapes to the UI's `MyPay`/`Payslip`
 * type (C7 PR B, #21938). Pure, no React, no network — mirrors
 * `wagepro_store_pay_runs.ts`/`wagepro_store_settings.ts`'s adapter pattern
 * exactly: the `Real*` interfaces are narrow, LOCALLY-declared subsets of
 * the real wire shapes (never imported from `apps/api` — this stays a
 * `data/` module, and `lib/trpc.ts` is the only module allowed to depend on
 * `ledger-api`), and the `derive*`/`resolve*` functions are the permanent
 * seam `use_my_pay.ts` calls.
 *
 * ── Why this is a self-fetching hook's adapter, not a `dev_*.ts` resolver ──
 *
 * Every other epic module (C2-C6) resolves real data through a
 * `dev_*.ts`/`useDevBackendData` boot-time seed — this deliberately does
 * NOT follow that pattern. C7 is real production OAuth-gated auth work
 * (#21938), not the C1 dev-bypass convenience the `dev_*.ts` layer exists
 * for. And the step-up retry flow (`use_my_pay.ts`'s `retry`) needs a
 * genuine re-fetch after the user re-authenticates — a boot-time
 * `useState` seed can't express that. Boot-blocking the WHOLE app
 * (including the employer admin console) on an ESS query that FORBIDs for
 * every non-2FA/non-ESS-linked user would also be a regression — so
 * `use_my_pay.ts` fetches lazily, only when the My Pay screen itself
 * mounts.
 *
 * ── Two calls, not three ────────────────────────────────────────────────
 *
 * `getPayslip` is allowlisted server-side but unused here: `listPayslips`
 * already returns whole `payslip_snapshots` rows, so a payslip detail view
 * selects from the already-fetched list rather than re-fetching one row by
 * id — confirmed against `apps/tax-web/src/screens/pay_payslips_screen.tsx`,
 * which calls the identical pair (`listEmployerLinks` + `listPayslips`) and
 * never `getPayslip` either.
 *
 * ── Documented gaps (do not silently drop) ──────────────────────────────
 *
 * 1. No allowance split — a `payslip_snapshots` row only has a single
 *    `gross_pay_cents` figure, no itemised allowances. Blocked on the same
 *    paused `epic/payroll-pay-items` as C4's pay-run line items.
 * 2. No EPF rate stored on the snapshot — only the computed
 *    `employee_epf_cents` amount. {@link EPF_EMPLOYEE_RATE_BP} pins the
 *    statutory 8% rate as a wagepro-web-local constant (matches the real SL
 *    engine's `SL_EMPLOYEE_EPF_BPS` exactly — see
 *    `packages/jumpstone-core/src/jurisdictions/lk/payroll/sl_payroll.ts`
 *    and, for the backend-verified byte-match, `payroll.getStatutoryPolicy`,
 *    #22726) — never imported from `apps/api`. It is kept on `PayslipLine`'s
 *    `epf_employee` variant
 *    for a future correct use, but is DELIBERATELY NOT interpolated into
 *    the displayed label (`wp.my_pay.line.epf_employee` is plain "EPF", no
 *    `{rate}`) — EPF is computed on basic salary, not on the single
 *    `gross_pay` figure this adapter actually has (gap #1), so
 *    `employee_epf_cents / gross_pay_cents` does NOT equal 8% whenever an
 *    allowance is present, and printing "EPF (8%)" next to a smaller
 *    percentage's worth of actual deduction would be a materially
 *    misleading figure on a real payslip (adversarial review finding, PR
 *    #22237) — worse than showing no rate at all.
 * 3. No `paid_on`/status distinction — `payslip_snapshots` rows are only
 *    ever created by the `approvePayRun` snapshot-hook, so a row existing
 *    at all implies an already-approved (and, by this app's UI copy,
 *    effectively "paid") pay run. There is no separate pending state left
 *    to represent.
 * 4. No `job_title`/`department` anywhere backend-side for the CALLER — C3's
 *    own documented gap, unresolved here either. `MyPay` carries
 *    `employer_name` (from `listEmployerLinks`' resolved `entity_name`)
 *    instead; the employee's own display name comes from the real session
 *    (`useSession().session.name`), not from this adapter.
 * 5. **CRITICAL — reconciliation.** `gross - epf - paye` does not always
 *    equal `net` — `pay_run_lines.deductions_cents` (custom, non-statutory
 *    deductions) is a TOTAL that never gets copied onto the snapshot. Every
 *    payslip MUST still visually reconcile (its own lines must sum to its
 *    own net pay), so {@link derivePayslipLines} always emits a residual
 *    line covering exactly the gap: a positive residual is an
 *    `other_deductions` line, a negative residual is an `other_earnings`
 *    line (rendered with its absolute value), and a zero residual emits no
 *    extra line at all. This is the single highest-value correctness
 *    decision in this module — a payslip whose own arithmetic doesn't add
 *    up is a real bug, not a cosmetic gap.
 * 6. `currency_code` is a free `text` column defaulting `'LKR'` — narrowed
 *    unconditionally to the single-value `CurrencyCode` union, the same
 *    unconditional stance `wagepro_store_pay_runs.ts` takes for its own
 *    `currency: 'LKR'` (that module's gap note applies verbatim here; this
 *    is not a cast, just a fixed literal, since `CurrencyCode` itself has
 *    exactly one member today).
 *
 * `employer_epf_cents`/`etf_cents` (employer-borne EPF/ETF contributions)
 * and `voided_at` are deliberately absent from {@link RealPayslipSnapshot}
 * below, even though they exist on the real `payslip_snapshots` row —
 * employer-borne contributions must NEVER appear as a line on an employee's
 * own payslip (they are not the employee's deduction), and the server
 * already filters voided snapshots out of what `listPayslips`/`getPayslip`
 * return, so this adapter never needs to reason about them.
 */
import type {
  BasisPoints,
  CurrencyCode,
  MyPay,
  Payslip,
  PayslipLine,
} from './types';
import {
  derivePayPeriod,
  deriveValidPeriodDate,
} from './wagepro_store_pay_runs';

/** One `personalPayroll.listEmployerLinks` row — see that procedure's response shape in `apps/api/src/trpc/routers/personal_payroll.ts`. */
export interface RealEmployerLink {
  id: string;
  entity_id: string;
  employee_id: string;
  entity_name: string;
  linked_at: string;
}

/**
 * A `payslip_snapshots` row, narrowed to exactly the fields this adapter
 * reads (`apps/api/src/db/schema/personal/payslip_snapshots.ts`).
 * Deliberately omits `workspace_id`/`entity_id`/`employee_id` (routing
 * fields, not display data), `employer_epf_cents`/`etf_cents`
 * (employer-borne — see module docblock), and `voided_at` (the server
 * already filters voided snapshots out).
 */
export interface RealPayslipSnapshot {
  id: string;
  pay_run_id: string;
  period_start: string;
  period_end: string;
  gross_pay_cents: number;
  net_pay_cents: number;
  employee_epf_cents: number;
  paye_cents: number;
  currency_code: string;
  created_at: string;
}

/** Today's only supported jurisdiction's statutory EPF employee rate — see module docblock's gap #2. Matches the real SL engine's `SL_EMPLOYEE_EPF_BPS` exactly (pinned as a literal in `wagepro_store_my_pay.test.ts`; independently backend-verified in `payroll.getStatutoryPolicy`'s test, #22726). */
export const EPF_EMPLOYEE_RATE_BP: BasisPoints = 800;

/** `currency_code` is real-shaped free text; this adapter unconditionally narrows to `CurrencyCode`'s single member — see module docblock's gap #6. */
const CURRENCY: CurrencyCode = 'LKR';

/**
 * Polarizes a raw signed cents value against its NORMAL direction (whether
 * it usually adds to or subtracts from net pay), flipping `is_deduction`
 * (and taking the absolute value) whenever the raw value's sign disagrees
 * with that normal direction.
 *
 * None of `gross_pay_cents`/`employee_epf_cents`/`paye_cents` carry a SQL
 * CHECK forbidding a negative value (plain `integer` columns — see
 * `apps/api/src/db/schema/personal/payslip_snapshots.ts`), so a correction
 * or refund run could in principle post one negative. Before this guard, a
 * negative `employee_epf_cents` (e.g. an EPF refund) still rendered as
 * `is_deduction: true`, which `format_money_deduction` (`lib/format.ts`)
 * displays with a leading "−" over `Math.abs(amount)` — literally the
 * opposite sign of what actually happened, even though the OVERALL
 * reconciliation (this line's contribution to the lines-sum-to-net
 * invariant) was already correct either way, since `residual` absorbs
 * whatever these three lines don't (adversarial review finding, PR
 * #22237). This function makes the PER-LINE display sign correct too, with
 * no change to the aggregate math: `normalizeSign(cents, dir).is_deduction
 * ? -amount : amount` is algebraically identical to `dir ? -cents : cents`
 * for every input, negative or not.
 */
function normalizeSign(
  cents: number,
  normallyDeduction: boolean,
): { amount: number; is_deduction: boolean } {
  const netContribution = normallyDeduction ? -cents : cents;
  return netContribution >= 0
    ? { amount: netContribution, is_deduction: false }
    : { amount: -netContribution, is_deduction: true };
}

/**
 * The lines for one payslip, always reconciling to the snapshot's own
 * `net_pay_cents` — see module docblock's gap #5. `gross_pay`/
 * `epf_employee`/`apit_withheld` are always present (even when `0`, e.g. an
 * all-zero snapshot); the residual line is appended only when
 * `gross - epf - paye - net !== 0`. Each of the three base lines has its
 * displayed sign normalized against its NORMAL direction (see
 * {@link normalizeSign}) rather than assuming the underlying cents value is
 * always non-negative.
 */
export function derivePayslipLines(
  snapshot: RealPayslipSnapshot,
): PayslipLine[] {
  const gross = normalizeSign(snapshot.gross_pay_cents, false);
  const epf = normalizeSign(snapshot.employee_epf_cents, true);
  const apit = normalizeSign(snapshot.paye_cents, true);

  const lines: PayslipLine[] = [
    {
      kind: 'gross_pay',
      amount: gross.amount,
      is_deduction: gross.is_deduction,
    },
    {
      kind: 'epf_employee',
      amount: epf.amount,
      is_deduction: epf.is_deduction,
      rate: EPF_EMPLOYEE_RATE_BP,
    },
    {
      kind: 'apit_withheld',
      amount: apit.amount,
      is_deduction: apit.is_deduction,
    },
  ];

  const residual =
    snapshot.gross_pay_cents -
    snapshot.employee_epf_cents -
    snapshot.paye_cents -
    snapshot.net_pay_cents;

  if (residual > 0) {
    lines.push({
      kind: 'other_deductions',
      amount: residual,
      is_deduction: true,
    });
  } else if (residual < 0) {
    lines.push({
      kind: 'other_earnings',
      amount: -residual,
      is_deduction: false,
    });
  }

  return lines;
}

/**
 * `wagepro_store_employees.ts`'s `isParseableIsoDate` assumes a DATE-only
 * string (it appends `T00:00:00Z` before parsing) -- `created_at` here is
 * already a full ISO datetime (`…T08:00:00Z`), so reusing that helper
 * unconditionally fails every real value (`…T08:00:00ZT00:00:00Z` is not
 * parseable). This checks the datetime string as-is instead.
 */
function isParseableIsoDateTime(value: string): boolean {
  return !isNaN(new Date(value).getTime());
}

function toPayslip(snapshot: RealPayslipSnapshot): Payslip {
  const periodStart = deriveValidPeriodDate(
    snapshot.period_start,
    snapshot.created_at,
  );
  return {
    id: snapshot.id,
    pay_run_id: snapshot.pay_run_id,
    period: derivePayPeriod(periodStart),
    currency: CURRENCY,
    // Falls back to the already-validated period start when `created_at`
    // itself is calendar-invalid, rather than slicing a potentially
    // unparseable string straight into a date `format_date` (`lib/
    // format.ts`) would throw a RangeError on -- this app has no
    // ErrorBoundary anywhere, so an unguarded bad date here would white
    // -screen the whole My Pay screen (adversarial review finding, PR
    // #22237).
    issued_on: isParseableIsoDateTime(snapshot.created_at)
      ? snapshot.created_at.slice(0, 10)
      : periodStart,
    lines: derivePayslipLines(snapshot),
    net_pay: snapshot.net_pay_cents,
  };
}

/**
 * Derives `MyPay` from the caller's own payslip snapshots (already scoped
 * to one employer entity — `listPayslips` takes an `entity_id`), or
 * `undefined` when there are none yet. `snapshots` need not be
 * pre-sorted — this defensively re-sorts by `period_start` descending so
 * the most recent snapshot is always `current_payslip` regardless of the
 * order the caller resolved them in. Ties on `period_start` (nothing stops
 * two pay runs — an off-cycle bonus run, a correction run — from sharing a
 * period, `pay_run_id` is the only unique column) are broken by
 * `created_at` then `id`, both descending, so the ordering is fully
 * deterministic rather than depending on SQLite's unspecified order for
 * equal sort keys (adversarial review finding, PR #22237).
 */
export function deriveMyPay(
  snapshots: readonly RealPayslipSnapshot[],
  employerName: string,
): MyPay | undefined {
  if (snapshots.length === 0) return undefined;

  const sorted = [...snapshots].sort((a, b) => {
    const byPeriod = b.period_start.localeCompare(a.period_start);
    if (byPeriod !== 0) return byPeriod;
    const byCreatedAt = b.created_at.localeCompare(a.created_at);
    if (byCreatedAt !== 0) return byCreatedAt;
    return b.id.localeCompare(a.id);
  });
  const [mostRecent, ...rest] = sorted;

  return {
    employer_name: employerName,
    current_payslip: toPayslip(mostRecent),
    // Same `toPayslip` as the current one: an earlier payslip is a payslip,
    // and the screen lets the employee open it. Previously mapped down to
    // `{ period, net_pay }`, which discarded line items already fetched.
    history: rest.map(toPayslip),
  };
}

// ── Screen state resolution ─────────────────────────────────────────────────

/** How a `listEmployerLinks`/`listPayslips` call can fail, as classified by `isAal2Required` (`lib/auth/step_up.ts`). */
export type MyPayLoadFailure = 'step_up' | 'error';

export type MyPayState =
  | { kind: 'loading' }
  | { kind: 'step_up_required' }
  | { kind: 'error' }
  | { kind: 'no_employer_link' }
  | {
      kind: 'no_payslips';
      employers: RealEmployerLink[];
      selected_entity_id: string;
    }
  | {
      kind: 'ready';
      my_pay: MyPay;
      employers: RealEmployerLink[];
      selected_entity_id: string;
    };

/**
 * Pure state resolver for the My Pay screen — no network, no React. `use_
 * my_pay.ts` is the only caller; every input field mirrors that hook's own
 * `useState` slots exactly, so this function's precedence order IS the
 * screen's actual behavior, not just a description of it.
 *
 * Precedence (fail-closed first):
 *   1. `step_up` on EITHER call outranks everything else, including `error`
 *      on the other call — a token can downgrade to AAL1 mid-flow (between
 *      the links call and the payslips call), and the actionable step-up CTA
 *      must win over a generic error state.
 *   2. `error` on either call.
 *   3. `links` not yet resolved -> `loading`.
 *   4. `links` resolved but empty -> `no_employer_link` (the invite hasn't
 *      been accepted yet — see `data/use_my_pay.ts`'s docblock on why
 *      `acceptEssInvite` is never wired here).
 *   5. `selected_entity_id` not yet chosen (the resolver defaults it to
 *      `links[0]` the same render links resolve, but this state is still
 *      reachable for one tick) -> `loading`, never a crash or a
 *      wrong-employer `ready`.
 *   6. `snapshots` not yet resolved for the selected employer -> `loading`.
 *   7. `snapshots` resolved but empty -> `no_payslips`.
 *   8. Otherwise -> `ready`, with `my_pay.employer_name` resolved from the
 *      SELECTED link (never `links[0]` unconditionally) — the
 *      multi-employer wrong-data regression guard.
 */
export function resolveMyPayState(input: {
  links: RealEmployerLink[] | undefined;
  links_failure: MyPayLoadFailure | undefined;
  selected_entity_id: string | null;
  snapshots: RealPayslipSnapshot[] | undefined;
  snapshots_failure: MyPayLoadFailure | undefined;
}): MyPayState {
  if (
    input.links_failure === 'step_up' ||
    input.snapshots_failure === 'step_up'
  ) {
    return { kind: 'step_up_required' };
  }
  if (input.links_failure === 'error' || input.snapshots_failure === 'error') {
    return { kind: 'error' };
  }
  if (input.links === undefined) {
    return { kind: 'loading' };
  }
  if (input.links.length === 0) {
    return { kind: 'no_employer_link' };
  }
  if (input.selected_entity_id === null) {
    return { kind: 'loading' };
  }
  if (input.snapshots === undefined) {
    return { kind: 'loading' };
  }
  if (input.snapshots.length === 0) {
    return {
      kind: 'no_payslips',
      employers: input.links,
      selected_entity_id: input.selected_entity_id,
    };
  }

  const employerName =
    input.links.find((link) => link.entity_id === input.selected_entity_id)
      ?.entity_name ?? '—';
  const myPay = deriveMyPay(input.snapshots, employerName);
  if (myPay === undefined) {
    // Unreachable in practice -- `input.snapshots.length === 0` is already
    // handled above, and that is the ONLY case `deriveMyPay` returns
    // `undefined` for. Guarded defensively rather than asserted with `!`.
    return {
      kind: 'no_payslips',
      employers: input.links,
      selected_entity_id: input.selected_entity_id,
    };
  }

  return {
    kind: 'ready',
    my_pay: myPay,
    employers: input.links,
    selected_entity_id: input.selected_entity_id,
  };
}
